(function () {
  const API_URL = 'https://trustpilot.vivolife.com/api';
  const CACHE_KEY = 'tp_ratings_cache';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour — adjust as needed
  const DEFAULT_FALLBACK_RATING = 5;
  const DEFAULT_FALLBACK_REVIEWS = 2422;

  // In-memory copy of the cache object, loaded once and reused across all elements
  let cacheStore = null;

  function loadCacheStore() {
    if (cacheStore) return cacheStore;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      cacheStore = raw ? JSON.parse(raw) : {};
    } catch (e) {
      cacheStore = {}; // corrupted or storage unavailable — start fresh
    }
    return cacheStore;
  }

  function persistCacheStore() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheStore));
    } catch (e) {
      // storage full or unavailable (e.g. private browsing) — fail silently, not critical
    }
  }

  function getCache(key) {
    const store = loadCacheStore();
    const entry = store[key];
    if (!entry || typeof entry.timestamp !== 'number') return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
    return entry;
  }

  function setCache(key, data) {
    const store = loadCacheStore();
    store[key] = { ...data, timestamp: Date.now() };
    persistCacheStore();
  }

  function applyRating(stars, text, rating, reviews) {
    if (stars && rating != null && !isNaN(rating)) {
      stars.style.setProperty('--rating-percent', `${(rating / 5) * 100}%`);
      stars.setAttribute('aria-label', `${Number(rating).toFixed(1)} out of 5 stars`);
    }
    if (text && reviews != null && !isNaN(reviews)) {
      text.textContent = `${Number(reviews).toLocaleString()} Reviews`;
    }
  }

  async function updateElement(el) {
    if (el.dataset.tpProcessed === 'true') return;
    el.dataset.tpProcessed = 'true';

    const stars = el.querySelector('[data-rating-stars]');
    const text = el.querySelector('[data-rating-reviews]');

    const fallbackRatingAttr = Number(el.getAttribute('fallback-rating'));
    const fallbackReviewsAttr = Number(el.getAttribute('fallback-reviews'));

    const fallbackRating = !isNaN(fallbackRatingAttr) && fallbackRatingAttr > 0
      ? fallbackRatingAttr
      : DEFAULT_FALLBACK_RATING;

    const fallbackReviews = !isNaN(fallbackReviewsAttr) && fallbackReviewsAttr > 0
      ? fallbackReviewsAttr
      : DEFAULT_FALLBACK_REVIEWS;

    applyRating(stars, text, fallbackRating, fallbackReviews);

    const skus = el.dataset.skus;
    if (!skus) return;

    const cached = getCache(skus);
    if (cached) {
      applyRating(stars, text, cached.rating, cached.reviews);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.set('sku', skus);

      const response = await fetch(`${API_URL}/stats?${params.toString()}`);
      if (!response.ok) throw new Error(`Stats request failed (${response.status})`);

      const data = await response.json();
      if (!data.success || !data.stats) throw new Error('Invalid stats response');

      const rating = data.stats.averageRating != null ? Number(data.stats.averageRating) : null;
      const reviews = data.stats.totalReviews != null ? Number(data.stats.totalReviews) : null;

      if (rating != null && !isNaN(rating) && reviews != null && !isNaN(reviews)) {
        applyRating(stars, text, rating, reviews);
        setCache(skus, { rating, reviews });
      }

    } catch (error) {
      console.warn(`Using fallback Trustpilot rating for SKU "${skus}".`, error);
    }
  }

  async function fetchTrustPilotProductReviews(root = document) {
    const ratingElements = root.querySelectorAll('[product-trustpilot-rating]:not([data-tp-processed="true"])');
    if (!ratingElements.length) return;
    await Promise.all(Array.from(ratingElements).map(updateElement));
  }

  function init() {
    fetchTrustPilotProductReviews();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;

          if (node.matches && node.matches('[product-trustpilot-rating]')) {
            updateElement(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('[product-trustpilot-rating]:not([data-tp-processed="true"])')
              .forEach(updateElement);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

window.addEventListener("load", function(){
  loadTrustpilotWidgets();
})

/**
 * Detects the storefront locale from a URL subpath (e.g. /en-uk/, /es-eu/, /de-eu/)
 * and loads/reloads Trustpilot widget(s) with the correct data-locale.
 */

// Map your storefront's subpath segment -> Trustpilot's expected locale code.
// Add/adjust entries here as you add more storefront locales.
var TRUSTPILOT_SUBPATH_LOCALE_MAP = {
  'en-uk': 'en-GB',
  'es-eu': 'es-ES',
  'es-uk': 'es-ES',
  'de-eu': 'de-DE',
  'de-uk': 'es-DE',
  'fr-eu': 'fr-FR',
  'fr-uk': 'fr-FR',
  'it-eu': 'it-IT',
  'it-uk': 'it-IT'
};

var TRUSTPILOT_DEFAULT_LOCALE = 'en-US';
var TRUSTPILOT_POLL_INTERVAL_MS = 100;
var TRUSTPILOT_POLL_TIMEOUT_MS = 6000;

function getTrustpilotLocaleFromPath() {
  var segments = window.location.pathname.split('/').filter(Boolean);
  var firstSegment = (segments[0] || '').toLowerCase();
  return TRUSTPILOT_SUBPATH_LOCALE_MAP[firstSegment] || TRUSTPILOT_DEFAULT_LOCALE;
}

function renderTrustpilotWidget(el) {
  if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
    window.Trustpilot.loadFromElement(el, true);
  }
}

function loadTrustpilotWidgets() {
  var widgets = document.querySelectorAll('[data-trustpilot-widget]');
  if (!widgets.length) return;

  var locale = getTrustpilotLocaleFromPath();

  widgets.forEach(function (el) {
    el.setAttribute('data-locale', locale);
  });

  if (window.Trustpilot) {
    widgets.forEach(renderTrustpilotWidget);
    return;
  }

  // Bootstrap script may still be loading (e.g. it's async) — poll briefly.
  var elapsed = 0;
  var pollId = setInterval(function () {
    elapsed += TRUSTPILOT_POLL_INTERVAL_MS;
    if (window.Trustpilot) {
      clearInterval(pollId);
      widgets.forEach(renderTrustpilotWidget);
    } else if (elapsed >= TRUSTPILOT_POLL_TIMEOUT_MS) {
      clearInterval(pollId);
      // Trustpilot script failed to load in time; widgets stay as fallback links.
    }
  }, TRUSTPILOT_POLL_INTERVAL_MS);
}